'use client';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Dialog } from '@base-ui/react/dialog';
import { ArrowUpRight, BookOpen, Building2, Layers3, Search, Users, X } from 'lucide-react';
import {
  publicSearchResults,
  searchCategories,
  type PublicSearchEntry,
  type SearchCategory,
} from '@/lib/marketing/search';

const icons = {
  Platform: Layers3,
  Solutions: Users,
  Industries: Building2,
  Resources: BookOpen,
  Company: Building2,
};
export function PublicSearch({
  index,
  variant = 'light',
  onOpen,
}: {
  index: readonly PublicSearchEntry[];
  variant?: 'light' | 'dark';
  onOpen?: () => void;
}) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<SearchCategory | 'All'>('All');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useId();
  const results = useMemo(
    () => publicSearchResults(index, query, category),
    [index, query, category]
  );
  function changeOpen(next: boolean) {
    setOpen(next);
    if (next) {
      setQuery('');
      setCategory('All');
      setActive(0);
      onOpen?.();
    }
  }
  const changeOpenRef = useRef(changeOpen);
  changeOpenRef.current = changeOpen;
  useEffect(() => {
    function shortcut(event: KeyboardEvent) {
      if (
        event.key.toLowerCase() === 'k' &&
        (event.ctrlKey || event.metaKey) &&
        !event.altKey &&
        !event.isComposing
      ) {
        event.preventDefault();
        changeOpenRef.current(!open);
      }
    }
    window.addEventListener('keydown', shortcut);
    return () => window.removeEventListener('keydown', shortcut);
  }, [open]);
  useEffect(() => {
    if (open) document.getElementById(`${list}-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, active, list, query, category]);
  function navigate(path: string) {
    changeOpen(false);
    router.push(path);
  }
  return (
    <Dialog.Root open={open} onOpenChange={changeOpen} modal>
      <Dialog.Trigger
        ref={trigger}
        aria-label="Search"
        title="Search public pages (Ctrl+K or Cmd+K)"
        className={`inline-flex h-10 w-10 items-center justify-center rounded-xl transition-colors ${variant === 'dark' ? 'text-white/80 hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100'}`}
      >
        <Search size={18} />
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Backdrop className="public-search-backdrop fixed inset-0 z-[200] bg-[#06162C]/65 backdrop-blur-sm" />
        <Dialog.Popup
          initialFocus={input}
          finalFocus={trigger}
          className="marketing-site public-search-popup fixed left-1/2 top-1/2 z-[201] flex w-[calc(100%-1.5rem)] max-w-[760px] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white text-[#0A1C33] shadow-[0_24px_90px_rgba(6,22,44,.3)] outline-none"
          onKeyDown={(event) => {
            if (event.nativeEvent.isComposing) return;
            if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && results.items.length) {
              event.preventDefault();
              setActive(
                (n) =>
                  (n + (event.key === 'ArrowDown' ? 1 : -1) + results.items.length) %
                  results.items.length
              );
            }
            if (event.key === 'Enter' && event.target === input.current && results.items[active]) {
              event.preventDefault();
              navigate(results.items[active].path);
            }
          }}
        >
          <Dialog.Title className="sr-only">Search Mentrox OneHR public pages</Dialog.Title>
          <Dialog.Description className="sr-only">
            Search product pages, industries, guides and company information. Use arrow keys to
            choose a result and Enter to open it.
          </Dialog.Description>
          <div className="shrink-0 border-b border-slate-200 bg-white">
            <div className="flex items-center gap-3 px-4 py-4 sm:px-5">
              <Search size={20} className="shrink-0 text-orange-700" />
              <input
                ref={input}
                type="search"
                role="combobox"
                aria-expanded="true"
                aria-autocomplete="list"
                aria-controls={list}
                aria-activedescendant={results.items[active] ? `${list}-${active}` : undefined}
                aria-label="Search Mentrox OneHR"
                placeholder="Search Mentrox OneHR..."
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActive(0);
                }}
                className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-slate-500"
              />
              <Dialog.Close
                aria-label="Close search"
                className="rounded-lg p-2 text-slate-600 hover:bg-slate-100"
              >
                <X size={18} />
              </Dialog.Close>
            </div>
            <div
              aria-label="Search category filters"
              className="flex flex-wrap gap-1.5 px-4 pb-3 sm:px-5"
            >
              {(['All', ...searchCategories] as const).map((value) => (
                <button
                  key={value}
                  type="button"
                  aria-pressed={value === category}
                  onClick={() => {
                    setCategory(value);
                    setActive(0);
                  }}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${category === value ? 'border-orange-200 bg-orange-50 text-orange-800' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                >
                  {value}
                </button>
              ))}
            </div>
          </div>
          <div className="min-h-0 overflow-y-auto overscroll-contain px-2 py-2">
            <p className="px-3 pb-2 pt-1 text-xs text-slate-500" role="status">
              {query.trim()
                ? `${results.items.length} of ${results.total} matches`
                : `${category === 'All' ? 'Popular destinations' : category + ' destinations'}`}
            </p>
            {results.items.length === 0 ? (
              <div className="px-4 py-7">
                <p className="text-base font-semibold">No matching pages</p>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Try a shorter phrase or choose another category.
                </p>
                <button
                  type="button"
                  className="mt-4 text-sm font-semibold text-orange-700"
                  onClick={() => {
                    setQuery('');
                    setCategory('All');
                    setActive(0);
                    input.current?.focus();
                  }}
                >
                  Clear search and filters
                </button>
              </div>
            ) : null}
            <div id={list} role="listbox" aria-label="Public page results">
              {results.items.map((item, i) => {
                const Icon = icons[item.category];
                const first = i === 0 || results.items[i - 1].category !== item.category;
                return (
                  <div key={item.path}>
                    {first && (
                      <p className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                        {item.category}
                      </p>
                    )}
                    <button
                      id={`${list}-${i}`}
                      role="option"
                      aria-selected={i === active}
                      tabIndex={-1}
                      type="button"
                      onMouseMove={() => setActive(i)}
                      onClick={() => navigate(item.path)}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left ${i === active ? 'bg-orange-50 ring-1 ring-inset ring-orange-200' : 'hover:bg-slate-50'}`}
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600">
                        <Icon size={15} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">
                          {item.name}
                          {item.draft && (
                            <span className="ml-2 text-[10px] font-normal text-amber-800">
                              Review draft
                            </span>
                          )}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-slate-600">
                          {item.description}
                        </span>
                      </span>
                      <ArrowUpRight size={14} className="shrink-0 text-slate-500" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap justify-between gap-2 border-t border-slate-200 bg-slate-50 px-5 py-2.5 text-[11px] text-slate-600">
            <span>↑ ↓ to choose · Enter to open</span>
            <span>Esc to close · Ctrl / ⌘ K</span>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
